import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FileText, RefreshCw, Loader2, FileSpreadsheet, Trash2, ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { statusColors, STATUS_OPTIONS, formatDate } from './constants';
import { DATE_PRESETS } from './dateRange';

export const ApplicationsTable = ({
  applications, loading, statusFilter, onStatusFilterChange,
  inquiryType = 'credit',
  onRefresh, onSelect,
  onStatusChange, onDelete,
  onExport, exporting,
  page = 1, totalPages = 1, totalRows = 0, onPageChange,
  pageSize = 20, onPageSizeChange,
  datePreset = 'all', onDatePresetChange,
  customFrom = '', customTo = '', onCustomFromChange, onCustomToChange,
  selectedIds = [], onToggleSelect, onToggleSelectAll, onBulkDelete, onBulkStatus,
}) => {
  const pageIds = applications.map((a) => a.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const someOnPageSelected = pageIds.some((id) => selectedIds.includes(id)) && !allOnPageSelected;
  const isContact = inquiryType === 'contact';
  const tableTitle = isContact ? 'Əlaqə mesajları' : 'Müraciətlərin siyahısı';
  const tableSubtitle = isContact
    ? '"Bizimlə əlaqə" formasından gələn mesajlar'
    : 'Bütün kredit müraciətlərini buradan idarə edin';
  const emptyText = isContact ? 'Mesaj tapılmadı' : 'Müraciət tapılmadı';
  return (
  <Card>
    <CardHeader>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <CardTitle className="text-xl">{tableTitle}</CardTitle>
          <CardDescription>{tableSubtitle}</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={onExport}
            variant="outline"
            size="sm"
            disabled={exporting || applications.length === 0}
            className="border-green-600 text-green-700 hover:bg-green-50"
            data-testid="export-excel-btn"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            <span className="ml-2">Excel-ə ixrac</span>
          </Button>
          <Button onClick={onRefresh} variant="outline" size="sm" disabled={loading} data-testid="refresh-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-2">Yenilə</span>
          </Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1.5 block uppercase tracking-wide">Status</label>
          <Select value={statusFilter} onValueChange={onStatusFilterChange}>
            <SelectTrigger data-testid="status-filter-select" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Bütün statuslar</SelectItem>
              {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-xs font-medium text-gray-600 mb-1.5 block uppercase tracking-wide flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5" />
            Tarix aralığı
          </label>
          <Select value={datePreset} onValueChange={onDatePresetChange}>
            <SelectTrigger data-testid="date-preset-select" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_PRESETS.map((p) => (
                <SelectItem key={p.value} value={p.value} data-testid={`date-preset-${p.value}`}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {datePreset === 'custom' ? (
          <>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block uppercase tracking-wide">Başlanğıc</label>
              <Input
                type="date"
                value={customFrom}
                onChange={(e) => onCustomFromChange(e.target.value)}
                max={customTo || undefined}
                data-testid="date-from-input"
                className="w-full"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block uppercase tracking-wide">Son</label>
              <Input
                type="date"
                value={customTo}
                onChange={(e) => onCustomToChange(e.target.value)}
                min={customFrom || undefined}
                data-testid="date-to-input"
                className="w-full"
              />
            </div>
          </>
        ) : (
          <div className="lg:col-start-4">
            <label className="text-xs font-medium text-gray-600 mb-1.5 block uppercase tracking-wide">Səhifə ölçüsü</label>
            <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
              <SelectTrigger data-testid="page-size-select" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="20" data-testid="page-size-20">20 / səhifə</SelectItem>
                <SelectItem value="50" data-testid="page-size-50">50 / səhifə</SelectItem>
                <SelectItem value="100" data-testid="page-size-100">100 / səhifə</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {datePreset === 'custom' && (
        <div className="mt-3">
          <label className="text-xs font-medium text-gray-600 mb-1.5 block uppercase tracking-wide">Səhifə ölçüsü</label>
          <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
            <SelectTrigger data-testid="page-size-select" className="max-w-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="20" data-testid="page-size-20">20 / səhifə</SelectItem>
              <SelectItem value="50" data-testid="page-size-50">50 / səhifə</SelectItem>
              <SelectItem value="100" data-testid="page-size-100">100 / səhifə</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </CardHeader>
    <CardContent>
      {/* Bulk-action bar (visible when at least one row is selected) */}
      {selectedIds.length > 0 && (
        <div
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5"
          data-testid="bulk-action-bar"
        >
          <p className="text-sm text-blue-900">
            <span className="font-semibold">{selectedIds.length}</span> müraciət seçildi
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Select
              value=""
              onValueChange={(v) => { if (v) onBulkStatus(v); }}
            >
              <SelectTrigger
                data-testid="bulk-status-select"
                className="h-9 w-[180px] text-sm bg-white"
              >
                <SelectValue placeholder="Toplu status dəyişdir" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`bulk-status-option-${s}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={onBulkDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              Seçilənləri sil
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : applications.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>{emptyText}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="py-3 pl-4 pr-2 w-10">
                  <Checkbox
                    checked={allOnPageSelected}
                    onCheckedChange={onToggleSelectAll}
                    aria-label="Bu səhifədəkilərin hamısını seç"
                    data-testid="select-all-checkbox"
                    className={someOnPageSelected ? 'data-[state=unchecked]:bg-blue-200' : ''}
                  />
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Müştəri</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">{isContact ? 'Elektron ünvan' : 'Telefon'}</th>
                {!isContact && <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm hidden lg:table-cell">Ünvan</th>}
                {!isContact && <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Məbləğ</th>}
                {isContact && <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm hidden md:table-cell">Mesaj</th>}
                <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm hidden lg:table-cell">Tarix</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Status</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Əməliyyat</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((app) => (
                <tr key={app.id} className="border-b hover:bg-gray-50" data-testid={`application-row-${app.id}`}>
                  <td className="py-3 pl-4 pr-2 w-10">
                    <Checkbox
                      checked={selectedIds.includes(app.id)}
                      onCheckedChange={() => onToggleSelect(app.id)}
                      aria-label="Sıranı seç"
                      data-testid={`row-checkbox-${app.id}`}
                    />
                  </td>
                  <td className="py-3 px-4">
                    <div className="font-medium text-gray-900">{app.firstName} {app.lastName}</div>
                  </td>
                  <td className="py-3 px-4 text-gray-700 text-sm break-all" data-testid={`contact-cell-${app.id}`}>
                    {isContact ? (app.email || app.phone || '—') : (app.phone || '—')}
                  </td>
                  {!isContact && (
                    <td
                      className="py-3 px-4 text-gray-700 text-sm hidden lg:table-cell max-w-[220px] truncate"
                      title={app.address || ''}
                      data-testid={`address-cell-${app.id}`}
                    >
                      {app.address || '—'}
                    </td>
                  )}
                  {!isContact && <td className="py-3 px-4 font-semibold text-gray-900 text-sm">{app.loanAmount ? `${app.loanAmount} AZN` : '—'}</td>}
                  {isContact && (
                    <td
                      className="py-3 px-4 text-gray-700 text-sm hidden md:table-cell max-w-[320px] truncate"
                      title={app.message || ''}
                      data-testid={`message-cell-${app.id}`}
                    >
                      {app.message || '—'}
                    </td>
                  )}
                  <td className="py-3 px-4 text-sm text-gray-600 hidden lg:table-cell">{formatDate(app.createdAt)}</td>
                  <td className="py-3 px-4">
                    <Select
                      value={app.status}
                      onValueChange={(newStatus) => {
                        if (newStatus !== app.status) onStatusChange(app.id, newStatus);
                      }}
                    >
                      <SelectTrigger
                        data-testid={`status-select-${app.id}`}
                        className={`h-8 w-[140px] text-xs font-medium ${statusColors[app.status] || ''}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => (
                          <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => onSelect(app)} data-testid={`details-btn-${app.id}`}>
                        Ətraflı
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onDelete(app.id)}
                        data-testid={`delete-btn-${app.id}`}
                        className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 px-2"
                        aria-label="Müraciəti sil"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination footer */}
      {!loading && applications.length > 0 && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-4 pt-4 border-t border-gray-100">
          <p className="text-sm text-gray-600" data-testid="pagination-info">
            {(() => {
              const start = (page - 1) * pageSize + 1;
              const end = (page - 1) * pageSize + applications.length;
              return (
                <>
                  <span className="font-semibold text-gray-900">{start}</span>
                  {end > start && <>–<span className="font-semibold text-gray-900">{end}</span></>}
                  {' / '}
                  <span className="font-semibold text-gray-900">{totalRows}</span> müraciət
                  {totalPages > 1 && (
                    <> · Səhifə <span className="font-semibold text-gray-900">{page}</span> / {totalPages}</>
                  )}
                </>
              );
            })()}
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1.5">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onPageChange(Math.max(1, page - 1))}
                disabled={page <= 1}
                data-testid="pagination-prev-btn"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="ml-1 hidden sm:inline">Əvvəlki</span>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                data-testid="pagination-next-btn"
              >
                <span className="mr-1 hidden sm:inline">Növbəti</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </CardContent>
  </Card>
  );
};
